// src/services/gameService.ts
import { logger } from '../utils/logger';
// Proper implementation using games table with UUIDs
// 🔧 FIX: Handle duplicate BGG IDs by falling back to name matching

import { supabase } from './supabase';
import { UserGame, OwnershipStatus, TTRPGPreference } from '../types/games';
import { v4 as uuidv4 } from 'uuid';

export interface BoardGame {
  id: string;
  name: string;
  description: string;
  designer?: string;
  publisher?: string;
  yearPublished?: number;
  players?: { min: number; max: number };
  playTime?: number; // in minutes
  mechanics: string[];
  categories: string[];
  imageUrl?: string;
  rating?: number;
  weight?: number; // complexity rating
}

export interface GameCollection {
  id: string;
  userId: string;
  gameId: string;
  ownedCopy: boolean;
  wantToBuy: boolean;
  rating?: number;
  notes?: string;
  addedAt: string;
}

export interface GameReview {
  id: string;
  userId: string;
  gameId: string;
  rating: number;
  review: string;
  createdAt: string;
  username?: string;
  displayName?: string;
}

/**
 * Find or create a game in the games table
 * Returns the game's UUID
 * 🔧 FIX: Handle duplicate BGG IDs by checking for multiple results and falling back to name matching
 */
async function findOrCreateGame(gameData: {
  name: string;
  bgg_id?: number | string;
  game_type?: string;
  min_players?: number;
  max_players?: number;
  min_playtime?: number;
  max_playtime?: number;
  year_published?: number | string;
  short_name?: string;
  is_system?: boolean;
  image_url?: string;
}): Promise<{ success: boolean; gameId?: string; error?: string }> {
  try {
    // Normalize bgg_id
    const bggId = gameData.bgg_id ? String(gameData.bgg_id) : '0';

    // For non-custom games, try to find by BGG ID first
    if (bggId !== '0') {
      // 🆕 FIX: Check if there are duplicate BGG IDs (e.g., Clank!, Dark Souls)
      const { data: gamesWithSameBggId, error: searchError } = await supabase
        .from('games')
        .select('id, name')
        .eq('bgg_id', bggId);

      if (searchError) {
        logger.error('Error searching for game by bgg_id:', searchError);
      }

      if (gamesWithSameBggId && gamesWithSameBggId.length > 0) {
        // If we have multiple games with the same BGG ID, match by name
        if (gamesWithSameBggId.length > 1) {
          logger.log(`⚠️ Found ${gamesWithSameBggId.length} games with BGG ID ${bggId} - matching by name`);

          // 🆕 FIX: Normalize names by removing punctuation and extra spaces
          const normalizeForComparison = (name: string) => {
            return name
              .toLowerCase()
              .replace(/[^\w\s]/g, '') // Remove all punctuation
              .replace(/\s+/g, ' ')    // Collapse multiple spaces
              .trim();
          };

          const normalizedSearchName = normalizeForComparison(gameData.name);
          logger.log('Searching for:', gameData.name, '→', normalizedSearchName);

          const matchedGame = gamesWithSameBggId.find(game => {
            const normalized = normalizeForComparison(game.name);
            logger.log('Comparing with:', game.name, '→', normalized);
            return normalized === normalizedSearchName;
          });

          if (matchedGame) {
            logger.log('✅ Found exact name match for duplicate BGG ID:', matchedGame.name, matchedGame.id);
            return { success: true, gameId: matchedGame.id };
          }

          // If no exact match, try partial match with normalized names
          const partialMatch = gamesWithSameBggId.find(game => {
            const normalized = normalizeForComparison(game.name);
            return normalized.includes(normalizedSearchName) ||
                   normalizedSearchName.includes(normalized);
          });

          if (partialMatch) {
            logger.log('✅ Found partial name match for duplicate BGG ID:', partialMatch.name, partialMatch.id);
            return { success: true, gameId: partialMatch.id };
          }

          // If still no match, we'll create a new entry below
          logger.log('⚠️ No name match found among duplicates, will create new entry');
        } else {
          // Only one game with this BGG ID - use it
          logger.log('✅ Found existing game by bgg_id:', gamesWithSameBggId[0].id);
          return { success: true, gameId: gamesWithSameBggId[0].id };
        }
      }
    } else {
      // For custom games (bgg_id = '0'), search by name to avoid duplicates
      const { data: existingGame, error: searchError } = await supabase
        .from('games')
        .select('id')
        .eq('name', gameData.name)
        .eq('bgg_id', '0')
        .maybeSingle();

      if (searchError) {
        logger.error('Error searching for custom game by name:', searchError);
      }

      if (existingGame) {
        logger.log('Found existing custom game by name:', existingGame.id);
        return { success: true, gameId: existingGame.id };
      }
    }

    // Game doesn't exist, create it
    const newGameId = uuidv4();
    const { data: newGame, error: insertError } = await supabase
      .from('games')
      .insert({
        id: newGameId,
        name: gameData.name,
        bgg_id: bggId,
        game_type: gameData.game_type || 'boardgame',
        min_players: gameData.min_players,
        max_players: gameData.max_players,
        min_playtime: gameData.min_playtime,
        max_playtime: gameData.max_playtime,
        year_published: gameData.year_published ? String(gameData.year_published) : null,
        short_name: gameData.short_name,
        is_system: gameData.is_system || false,
        image_url: gameData.image_url,
        is_custom: bggId === '0' && !gameData.is_system, // Custom if no BGG ID and not a known system
      })
      .select('id')
      .single();

    if (insertError) {
      logger.error('Error creating game:', insertError);
      return { success: false, error: insertError.message };
    }

    logger.log('Created new game:', newGame.id);
    return { success: true, gameId: newGame.id };
  } catch (error) {
    logger.error('Unexpected error in findOrCreateGame:', error);
    return { success: false, error: 'Failed to find or create game' };
  }
}

/**
 * Get all games in user's collection
 */
export async function getUserGames(): Promise<UserGame[]> {
  try {
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {
      return [];
    }

    const { data, error } = await supabase
      .from('user_games')
      .select(`
        id,
        user_id,
        game_id,
        ownership_status,
        ttrpg_preference,
        created_at,
        games:game_id (
          id,
          name,
          game_type,
          min_players,
          max_players,
          min_playtime,
          max_playtime,
          year_published
        )
      `)
      .eq('user_id', user.id);

    if (error) {
      logger.error('Error fetching user games:', error);
      return [];
    }

    // Transform the response to match UserGame interface
    // Filter out games where game data is missing (to avoid "Unknown Game" entries)
    return data
      .filter((item: any) => {
        const gameData = Array.isArray(item.games) ? item.games[0] : item.games;
        // Only include games where we have valid game data with a name
        return gameData && gameData.name && gameData.name !== 'Unknown Game';
      })
      .map((item: any) => {
        const gameData = Array.isArray(item.games) ? item.games[0] : item.games;

        return {
          id: item.id,
          user_id: item.user_id,
          game_id: item.game_id,
          name: gameData.name,
          game_type: gameData?.game_type ?? undefined,
          ownership_status: item.ownership_status as OwnershipStatus,
          ttrpg_preference: item.ttrpg_preference as TTRPGPreference,
          min_players: gameData?.min_players ?? undefined,
          max_players: gameData?.max_players ?? undefined,
          min_playtime: gameData?.min_playtime ?? undefined,
          max_playtime: gameData?.max_playtime ?? undefined,
          year_published: gameData?.year_published ? parseInt(gameData.year_published) : undefined,
          created_at: item.created_at,
        } as UserGame;
      });
  } catch (error) {
    logger.error('Error in getUserGames:', error);
    return [];
  }
}

/**
 * Add a board game to user's collection
 */
export async function addUserGame(gameData: {
  game_id?: number | string; // This is the BGG ID from search results
  name: string;
  game_type?: string;
  min_players?: number;
  max_players?: number;
  min_playtime?: number;
  max_playtime?: number;
  year_published?: number | string;
  short_name?: string;
  is_system?: boolean;
  image_url?: string;
}): Promise<{ success: boolean; game?: UserGame; error?: string }> {
  try {
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {
      return { success: false, error: 'Not authenticated' };
    }

    // Validate game data
    if (!gameData.name || gameData.name === 'Unknown Game') {
      logger.error('Invalid game name:', gameData);
      return { success: false, error: 'Invalid game name' };
    }

    logger.log('Adding game with data:', gameData);

    // Find or create the game in the games table and get its UUID
    const gameResult = await findOrCreateGame({
      name: gameData.name,
      bgg_id: gameData.game_id || 0,
      game_type: gameData.game_type || 'boardgame',
      min_players: gameData.min_players,
      max_players: gameData.max_players,
      min_playtime: gameData.min_playtime,
      max_playtime: gameData.max_playtime,
      year_published: gameData.year_published,
      short_name: gameData.short_name,
      is_system: gameData.is_system,
      image_url: gameData.image_url,
    });

    if (!gameResult.success || !gameResult.gameId) {
      return { success: false, error: gameResult.error || 'Failed to create game' };
    }

    // Check if user already has this game
    const { data: existing } = await supabase
      .from('user_games')
      .select('id')
      .eq('user_id', user.id)
      .eq('game_id', gameResult.gameId)
      .maybeSingle();

    if (existing) {
      return { success: false, error: 'You already have this game in your collection' };
    }

    // Add to user's collection using the UUID
    const { data, error } = await supabase
      .from('user_games')
      .insert({
        user_id: user.id,
        game_id: gameResult.gameId, // Use UUID from games table!
      })
      .select(`
        id,
        user_id,
        game_id,
        ownership_status,
        ttrpg_preference,
        created_at,
        games:game_id (
          id,
          name,
          game_type,
          min_players,
          max_players,
          min_playtime,
          max_playtime,
          year_published
        )
      `)
      .single();

    if (error) {
      logger.error('Error adding user game:', error);
      return { success: false, error: error.message };
    }

    // Handle the response - games might be an array or object
    const gameInfo = Array.isArray((data as any).games)
      ? (data as any).games[0]
      : (data as any).games;

    const game: UserGame = {
      id: data.id as string,
      user_id: data.user_id as string,
      game_id: data.game_id as string,
      name: gameInfo?.name || gameData.name,
      game_type: gameInfo?.game_type,
      ownership_status: data.ownership_status as OwnershipStatus,
      ttrpg_preference: data.ttrpg_preference as TTRPGPreference,
      min_players: gameInfo?.min_players ?? undefined,
      max_players: gameInfo?.max_players ?? undefined,
      min_playtime: gameInfo?.min_playtime ?? undefined,
      max_playtime: gameInfo?.max_playtime ?? undefined,
      year_published: gameInfo?.year_published ? parseInt(gameInfo.year_published) : undefined,
      created_at: data.created_at as string,
    };

    return { success: true, game };
  } catch (error) {
    logger.error('Error in addUserGame:', error);
    return { success: false, error: 'Failed to add game' };
  }
}

/**
 * Add a custom game (no BGG ID)
 */
export async function addCustomGame(gameData: {
  name: string;
  game_type: 'boardgame' | 'rpg';
  min_players?: number;
  max_players?: number;
  year_published?: number;
}): Promise<{ success: boolean; game?: UserGame; error?: string }> {
  // Custom games don't have BGG IDs, so we just call addUserGame without game_id
  return addUserGame({
    ...gameData,
    game_id: 0, // No BGG ID for custom games
  });
}

/**
 * Update a user's game preferences
 */
export async function updateUserGame(
  gameId: string,
  updates: {
    ownership_status?: OwnershipStatus;
    ttrpg_preference?: TTRPGPreference;
    skill_level?: string;
    favorite?: boolean;
  }
): Promise<{ success: boolean; error?: string }> {
  const { error } = await supabase
    .from('user_games')
    .update(updates)
    .eq('id', gameId);

  if (error) {
    logger.error('Error updating user game:', error);
    return { success: false, error: error.message };
  }

  return { success: true };
}

/**
 * Remove a game from user's collection
 */
export async function removeUserGame(gameId: string): Promise<{ success: boolean; error?: string }> {
  const { error } = await supabase
    .from('user_games')
    .delete()
    .eq('id', gameId);

  if (error) {
    logger.error('Error removing user game:', error);
    return { success: false, error: error.message };
  }

  return { success: true };
}

/**
 * Search games in the database by name
 * Returns up to 20 matching games
 */
async function searchGames(query: string): Promise<any[]> {
  try {
    if (!query || query.trim().length < 2) {
      return [];
    }

    const searchTerm = `%${query.trim()}%`;

    const { data, error } = await supabase
      .from('games')
      .select('id, bgg_id, name, game_type, min_players, max_players, min_playtime, max_playtime, year_published, short_name, is_system, image_url')
      .ilike('name', searchTerm)
      .limit(20);

    if (error) {
      logger.error('Error searching games:', error);
      return [];
    }

    return data || [];
  } catch (error) {
    logger.error('Error in searchGames:', error);
    return [];
  }
}

export const gameService = {
  getUserGames,
  addUserGame,
  addCustomGame,
  updateUserGame,
  removeUserGame,
  searchGames,
};